import logger from '../utils/logger';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import { API_BASE_URL } from '../config/api';

class WebSocketService {
    constructor() {
        this.stompClient = null;
        this.connected = false;
        this.subscriptions = new Map();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
    }

    connect(token) {
        return new Promise((resolve, reject) => {
            try {
                // Derive WS base: if API_BASE_URL ends with /api remove it to avoid double /api/api
                let base = API_BASE_URL;
                try {
                    if (base.endsWith('/api')) base = base.slice(0, -4);
                } catch (_) { /* ignore */ }
                const wsUrl = `${base}/ws`;
                logger.debug('Connecting SockJS to', wsUrl);
                const socket = new SockJS(wsUrl);
                this.stompClient = Stomp.over(socket);
                
                this.stompClient.connect(
                    { 'Authorization': `Bearer ${token}` },
                    () => {
                        logger.info('WebSocket connected successfully');
                        this.connected = true;
                        this.reconnectAttempts = 0;
                        resolve();
                    },
                    (error) => {
                        logger.error('WebSocket connection failed:', error);
                        this.connected = false;
                        this.handleReconnect(token, resolve, reject);
                    }
                );
            } catch (error) {
                logger.error('WebSocket connection error:', error);
                reject(error);
            }
        });
    }

    handleReconnect(token, resolve, reject) {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            logger.info(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            
            setTimeout(() => {
                this.connect(token)
                    .then(resolve)
                    .catch(reject);
            }, 2000 * this.reconnectAttempts);
        } else {
            logger.error('Max reconnection attempts reached');
            reject(new Error('Failed to connect to WebSocket'));
        }
    }

    subscribeToOrders(shopId, callback) {
        if (!this.connected || !this.stompClient) {
            logger.error('WebSocket not connected');
            return null;
        }

        const destination = `/topic/shop/${shopId}/orders`;
        const subscription = this.stompClient.subscribe(destination, (message) => {
            try {
                if (!message?.body) return;
                // Fast path JSON parse
                const order = JSON.parse(message.body);
                callback(order);
            } catch (error) {
                if (typeof message.body === 'string' && message.body.startsWith('<')) {
                    logger.error('Received non-JSON (HTML) payload on orders topic – likely wrong WS endpoint or server fallback. First 120 chars:', message.body.slice(0,120));
                }
                logger.error('Error parsing order message:', error);
            }
        });

        this.subscriptions.set(destination, subscription);
    logger.info(`Subscribed to orders for shop ${shopId}`);
        return subscription;
    }

    subscribeToShopUpdates(shopId, callback) {
        if (!this.connected || !this.stompClient) {
            logger.error('WebSocket not connected');
            return null;
        }

        const destination = `/topic/shop/${shopId}/updates`;
        const subscription = this.stompClient.subscribe(destination, (message) => {
            try {
                if (!message?.body) return;
                const update = JSON.parse(message.body);
                callback(update);
            } catch (error) {
                if (typeof message.body === 'string' && message.body.startsWith('<')) {
                    logger.error('Received non-JSON (HTML) payload on updates topic – likely wrong WS endpoint or server fallback. Snippet:', message.body.slice(0,120));
                }
                logger.error('Error parsing shop update message:', error);
            }
        });

        this.subscriptions.set(destination, subscription);
    logger.info(`Subscribed to updates for shop ${shopId}`);
        return subscription;
    }

    unsubscribe(destination) {
        const subscription = this.subscriptions.get(destination);
        if (subscription) {
            subscription.unsubscribe();
            this.subscriptions.delete(destination);
            logger.info(`Unsubscribed from ${destination}`);
        }
    }

    unsubscribeAll() {
        this.subscriptions.forEach((subscription, destination) => {
            subscription.unsubscribe();
            logger.info(`Unsubscribed from ${destination}`);
        });
        this.subscriptions.clear();
    }

    disconnect() {
        if (this.stompClient) {
            this.unsubscribeAll();
            this.stompClient.disconnect();
            this.connected = false;
            logger.info('WebSocket disconnected');
        }
    }

    isConnected() {
        return this.connected && this.stompClient && this.stompClient.connected;
    }
}

const websocketService = new WebSocketService();
export default websocketService; 